import React from "react";
import axios from "axios";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Diamond, Menu, X, Mail, UserCog, LogIn, User, LogOut, MousePointerClick, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pageTr } from "@/i18n/pageTranslations";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { to: "/labs", key: "labs" },
  { to: "/formation", key: "formation" },
  { to: "/treatments", key: "treatments" },
  { to: "/synthetics", key: "synthetics" },
  { to: "/journal", key: "journal" },
];

export const TopNav = ({ showBrand = true }) => {
  const { lang, setLang } = useLang();
  const t = pageTr[lang].nav;
  const { subscribed } = useSubscription();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [open, setOpen] = React.useState(false);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [authOpen, setAuthOpen] = React.useState(false);
  const profileRef = React.useRef(null);

  React.useEffect(() => {
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isSubscribed = subscribed || user?.subscription_active;

  const openPortal = async () => {
    try {
      let sid = user?.subscription_session_id;
      if (!sid) {
        const raw = localStorage.getItem("gemPriceSubscription");
        const obj = raw ? JSON.parse(raw) : null;
        sid = obj?.session_id;
      }
      if (!sid) {
        alert(lang === "fr"
          ? "Session d'abonnement introuvable."
          : "Subscription session not found.");
        return;
      }
      // Admin-granted subscriptions have no real Stripe session — show a clear notice.
      if (sid.startsWith("cs_admin_grant_")) {
        alert(lang === "fr"
          ? "Abonnement offert par l'équipe GEM PRICE. Pour toute modification, contactez gemprice@gemprice.org."
          : "Complimentary subscription granted by GEM PRICE. For any change, contact gemprice@gemprice.org.");
        return;
      }
      setPortalLoading(true);
      const { data } = await axios.post(`${API}/payments/portal`, {
        session_id: sid,
        return_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.portal_url;
    } catch (e) {
      setPortalLoading(false);
      alert(lang === "fr"
        ? "Impossible d'ouvrir le portail client pour le moment."
        : "Unable to open the customer portal right now.");
    }
  };
  return (
    <>
    <nav className="relative z-30 flex items-center justify-between px-6 md:px-14 py-5 border-b border-[#C1A661]/15 bg-[#050811]/80 backdrop-blur-sm">
      <Link to="/" className="flex items-center gap-2.5" data-testid="nav-brand">
        <img
          src="/brand/gemprice-icon-64.png"
          alt="GEM PRICE"
          width="28"
          height="28"
          className="shrink-0"
          style={{ imageRendering: "auto" }}
        />
        {showBrand && (
          <span className="font-serif text-2xl tracking-[0.18em] text-[#E2CFA4]">GEM PRICE</span>
        )}
      </Link>

      <div className="hidden md:flex items-center gap-1">
        {NAV.map((item, i) => (
          <NavLink
            key={item.key}
            to={item.to}
            data-testid={`nav-${item.key}`}
            className={({ isActive }) =>
              `px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase transition-colors ${
                isActive ? "text-[#C1A661]" : "text-[#A1A7B3] hover:text-[#E2CFA4]"
              }`
            }
          >
            <span className="text-[#C1A661] mr-1.5">{i + 1}.</span>
            {t[item.key]}
          </NavLink>
        ))}
      </div>

      <div className="flex items-center gap-3" ref={profileRef}>
        {isSubscribed && (
          <button
            onClick={openPortal}
            disabled={portalLoading}
            data-testid="topnav-account-btn"
            className="hidden md:flex items-center gap-2 rounded-full border border-[#C1A661]/50 hover:border-[#C1A661] bg-[#C1A661]/10 hover:bg-[#C1A661]/20 px-3.5 py-1.5 text-[11px] tracking-[0.2em] uppercase text-[#E2CFA4] transition-colors disabled:opacity-60"
          >
            <UserCog size={13} strokeWidth={1.5} className="text-[#C1A661]" />
            {lang === "fr" ? "Mon abonnement" : "My subscription"}
          </button>
        )}

        {/* Profile menu — always shown, adapts to logged-in state */}
        {user ? (
          <div className="hidden md:block relative">
            <button
              onClick={() => setProfileOpen((v) => !v)}
              data-testid="topnav-profile-btn"
              className="flex items-center gap-2 rounded-full border border-[#C1A661]/30 hover:border-[#C1A661] px-2.5 py-1 transition-colors"
              aria-label="Profile"
            >
              {user.picture ? (
                <img src={user.picture} alt="" width="22" height="22" className="rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <User size={14} className="text-[#C1A661]" />
              )}
              <span className="text-[11px] tracking-[0.15em] text-[#E2CFA4] max-w-[140px] truncate">
                {user.name || user.email}
              </span>
            </button>
            {profileOpen && (
              <div
                data-testid="topnav-profile-menu"
                className="absolute right-0 top-full mt-2 w-64 rounded-sm border border-[#C1A661]/25 bg-[#0A0F1F] shadow-[0_10px_30px_rgba(0,0,0,0.6)] p-4 z-50"
              >
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#C1A661] mb-1">
                  {lang === "fr" ? "Compte" : "Account"}
                </div>
                <div className="text-[12px] text-[#E2CFA4] truncate">{user.email}</div>
                <div className={`mt-2 text-[10px] tracking-[0.2em] uppercase ${isSubscribed ? "text-[#C1A661]" : "text-[#8A8F99]"}`}>
                  {isSubscribed
                    ? (lang === "fr" ? "Abonnement actif" : "Active subscription")
                    : (lang === "fr" ? "Aucun abonnement" : "No subscription")}
                </div>
                <button
                  onClick={() => { setProfileOpen(false); navigate("/profile"); }}
                  data-testid="profile-view-btn"
                  className="mt-3 w-full text-left text-[11px] tracking-[0.15em] uppercase text-[#E2CFA4] hover:text-[#C1A661] flex items-center gap-2"
                >
                  <User size={12} />
                  {lang === "fr" ? "Voir mon profil" : "View profile"}
                </button>
                {isSubscribed && (
                  <button
                    onClick={() => { setProfileOpen(false); openPortal(); }}
                    data-testid="profile-manage-btn"
                    className="mt-3 w-full text-left text-[11px] tracking-[0.15em] uppercase text-[#E2CFA4] hover:text-[#C1A661] flex items-center gap-2"
                  >
                    <UserCog size={12} />
                    {lang === "fr" ? "Gérer mon abonnement" : "Manage subscription"}
                  </button>
                )}
                <button
                  onClick={async () => { setProfileOpen(false); await logout(); }}
                  data-testid="profile-logout-btn"
                  className="mt-3 w-full text-left text-[11px] tracking-[0.15em] uppercase text-[#A1A7B3] hover:text-[#E2CFA4] flex items-center gap-2"
                >
                  <LogOut size={12} />
                  {lang === "fr" ? "Se déconnecter" : "Sign out"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setAuthOpen(true)}
            data-testid="topnav-signin-btn"
            className="hidden md:flex items-center gap-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] px-3.5 py-1.5 text-[11px] tracking-[0.2em] uppercase text-[#E2CFA4] transition-colors"
          >
            <LogIn size={13} strokeWidth={1.5} className="text-[#C1A661]" />
            {lang === "fr" ? "Profil" : "Profile"}
          </button>
        )}

        <a
          href="mailto:gemprice@gemprice.org"
          data-testid="topnav-contact-email"
          className="hidden md:flex items-center gap-2 text-[11px] tracking-[0.18em] text-[#A1A7B3] hover:text-[#C1A661] transition-colors"
          aria-label="Contact"
        >
          <Mail size={13} strokeWidth={1.4} className="text-[#C1A661]" />
          <span>gemprice@gemprice.org</span>
        </a>
        <div className="hidden md:flex rounded-full border border-[#C1A661]/25 overflow-hidden">
          <button data-testid="topnav-lang-en" onClick={() => setLang("en")}
            className={`px-3 py-1 text-[11px] tracking-[0.2em] ${lang === "en" ? "bg-[#C1A661]/15 text-[#E2CFA4]" : "text-[#A1A7B3]"}`}>EN</button>
          <button data-testid="topnav-lang-fr" onClick={() => setLang("fr")}
            className={`px-3 py-1 text-[11px] tracking-[0.2em] ${lang === "fr" ? "bg-[#C1A661]/15 text-[#E2CFA4]" : "text-[#A1A7B3]"}`}>FR</button>
        </div>
        {/* Mobile language toggle with flags */}
        <div className="md:hidden flex items-center rounded-full border border-[#C1A661]/25 overflow-hidden" data-testid="mobile-lang-switcher">
          <button
            data-testid="mobile-lang-en"
            onClick={() => setLang("en")}
            aria-label="English"
            className={`flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.15em] ${lang === "en" ? "bg-[#C1A661]/15 text-[#E2CFA4]" : "text-[#A1A7B3]"}`}
          >
            <span aria-hidden="true">🇬🇧</span>EN
          </button>
          <div className="w-px bg-[#C1A661]/25 h-4" />
          <button
            data-testid="mobile-lang-fr"
            onClick={() => setLang("fr")}
            aria-label="Français"
            className={`flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.15em] ${lang === "fr" ? "bg-[#C1A661]/15 text-[#E2CFA4]" : "text-[#A1A7B3]"}`}
          >
            <span aria-hidden="true">🇫🇷</span>FR
          </button>
        </div>

        <button
          data-testid="mobile-nav-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden flex items-center gap-1.5 text-[#E2CFA4] rounded-full border border-[#C1A661]/40 px-2.5 py-1.5 hover:border-[#C1A661] hover:bg-[#C1A661]/10 transition-colors"
          aria-label="menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
          {!open && (
            <MousePointerClick
              size={13}
              strokeWidth={1.6}
              className="text-[#C1A661] animate-pulse"
              aria-hidden="true"
              data-testid="mobile-nav-tap-hint"
            />
          )}
        </button>
      </div>

      {/* Back button on inner pages (visible everywhere except home) */}
      {!isHome && !open && (
        <div className="absolute left-4 md:left-14 top-full mt-3 z-10">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            data-testid="back-to-site-btn"
            className="flex items-center gap-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] bg-[#050811]/80 backdrop-blur px-3.5 py-1.5 text-[10px] tracking-[0.2em] uppercase text-[#E2CFA4] hover:bg-[#C1A661]/10 transition-colors"
          >
            <ArrowLeft size={13} strokeWidth={1.6} className="text-[#C1A661]" />
            {lang === "fr" ? "Retour" : "Back"}
          </button>
        </div>
      )}

      {open && (
        <div className="absolute top-full left-0 right-0 md:hidden bg-[#050811] border-b border-[#C1A661]/20 flex flex-col">
          {NAV.map((item, i) => (
            <NavLink
              key={item.key}
              to={item.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-6 py-3 text-[11px] tracking-[0.22em] uppercase border-b border-[#C1A661]/10 ${
                  isActive ? "text-[#C1A661]" : "text-[#A1A7B3]"
                }`
              }
            >
              <span className="text-[#C1A661] mr-2">{i + 1}.</span>
              {t[item.key]}
            </NavLink>
          ))}
          <div className="flex px-6 py-3 gap-2">
            <button onClick={() => setLang("en")} className="text-[11px] text-[#E2CFA4]">EN</button>
            <span className="text-[#C1A661]/40">|</span>
            <button onClick={() => setLang("fr")} className="text-[11px] text-[#E2CFA4]">FR</button>
          </div>
          <a
            href="mailto:gemprice@gemprice.org"
            data-testid="mobile-nav-contact-email"
            className="flex items-center gap-2 px-6 py-3 text-[11px] tracking-[0.18em] uppercase text-[#C1A661] border-t border-[#C1A661]/10"
          >
            <Mail size={13} strokeWidth={1.4} />
            gemprice@gemprice.org
          </a>
          {isSubscribed && (
            <button
              onClick={() => { setOpen(false); openPortal(); }}
              data-testid="mobile-nav-account-btn"
              className="flex items-center gap-2 px-6 py-3 text-[11px] tracking-[0.18em] uppercase text-[#E2CFA4] border-t border-[#C1A661]/10 bg-[#C1A661]/10"
            >
              <UserCog size={13} strokeWidth={1.5} />
              {lang === "fr" ? "Mon abonnement" : "My subscription"}
            </button>
          )}
          {user ? (
            <>
              <button
                onClick={() => { setOpen(false); navigate("/profile"); }}
                data-testid="mobile-nav-profile-btn"
                className="flex items-center gap-2 px-6 py-3 text-[11px] tracking-[0.18em] uppercase text-[#E2CFA4] border-t border-[#C1A661]/10"
              >
                <User size={13} strokeWidth={1.5} className="text-[#C1A661]" />
                {lang === "fr" ? "Mon profil" : "My profile"} · {user.email}
              </button>
              <button
                onClick={async () => { setOpen(false); await logout(); }}
                data-testid="mobile-nav-logout-btn"
                className="flex items-center gap-2 px-6 py-3 text-[11px] tracking-[0.18em] uppercase text-[#A1A7B3] border-t border-[#C1A661]/10"
              >
                <LogOut size={13} strokeWidth={1.5} />
                {lang === "fr" ? "Se déconnecter" : "Sign out"}
              </button>
            </>
          ) : (
            <button
              onClick={() => { setOpen(false); setAuthOpen(true); }}
              data-testid="mobile-nav-signin-btn"
              className="flex items-center gap-2 px-6 py-3 text-[11px] tracking-[0.18em] uppercase text-[#E2CFA4] border-t border-[#C1A661]/10"
            >
              <LogIn size={13} strokeWidth={1.5} className="text-[#C1A661]" />
              {lang === "fr" ? "Profil / Se connecter" : "Sign in"}
            </button>
          )}
        </div>
      )}
    </nav>
    <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
};
